/* ==========================================================================
   HOPE BASE ADVISORY LLC — AWWWARDS MOTION SUITE ENGINE
   Lenis Smooth Scroll + Motion Physics + Magnetic Cursor + 3D Tilt
   ========================================================================== */

import Lenis from 'lenis';
import { animate, inView } from 'motion';

let lenisInstance = null;

export function initMotionSuite() {
  // Check user motion preferences
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    return;
  }

  initSmoothScroll();
  initAnchorScrolling();
  initMagneticButtons();
  init3DCardTiltAndSpotlight();
  initKineticScrollReveals();
  initStatCounterTickers();
}

/**
 * 1. Lenis Smooth Inertia Momentum Scrolling
 */
function initSmoothScroll() {
  lenisInstance = new Lenis({
    autoRaf: true,
    lerp: 0.085,
    duration: 1.2,
    smoothWheel: true,
    wheelMultiplier: 0.95,
    touchMultiplier: 1.2,
    prevent: (node) => {
      // Allow native scrolling inside modals without Lenis capture
      return (
        node.classList.contains('admin-portal-modal') ||
        Boolean(node.closest('.admin-portal-modal')) ||
        node.classList.contains('invoice-studio-modal') ||
        Boolean(node.closest('.invoice-studio-modal')) ||
        node.classList.contains('admin-modal-backdrop') ||
        Boolean(node.closest('.admin-modal-backdrop')) ||
        node.classList.contains('admin-table-wrapper') ||
        Boolean(node.closest('.admin-table-wrapper'))
      );
    }
  });

  window.hbaLenis = lenisInstance;
}

/**
 * 2. Lenis Smooth Anchor Navigation
 */
function initAnchorScrolling() {
  if (!lenisInstance) return;

  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', (e) => {
      const href = anchor.getAttribute('href');
      if (!href || href === '#' || href.startsWith('#admin') || href.startsWith('#doc') || href.startsWith('#quo') || href.startsWith('#inv')) {
        return;
      }

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        lenisInstance.scrollTo(target, {
          offset: -75,
          duration: 1.2,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) // iconic expo out
        });
      }
    });
  });
}

/**
 * 3. Magnetic Interactive Buttons & Pills
 */
function initMagneticButtons() {
  const magneticElements = document.querySelectorAll(
    '.btn-pill, .theme-toggle, .admin-icon-btn, .sector-tab, .map-tab-btn'
  );

  magneticElements.forEach(el => {
    el.classList.add('magnetic-btn');

    el.addEventListener('mousemove', (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      // Subtle spring pull
      const strength = 0.22;
      el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
    });

    el.addEventListener('mouseleave', () => {
      // Spring back to origin
      el.style.transform = 'translate(0px, 0px)';
    });
  });
}

/**
 * 4. 3D Perspective Card Tilt & Radial Spotlight Borders
 */
function init3DCardTiltAndSpotlight() {
  const cards = document.querySelectorAll(
    '.service-card, .feature-card, .step-card, .case-card, .matrix-card, .insight-card, .proof-card, .contact-cta-banner'
  );

  cards.forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Update CSS variables for radial spotlight glow
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);

      // 3D subtle perspective tilt
      const xPct = (x / rect.width) - 0.5;
      const yPct = (y / rect.height) - 0.5;
      const rotX = yPct * -6; // max 3deg tilt
      const rotY = xPct * 6;  // max 3deg tilt

      card.style.transform = `perspective(1000px) rotateX(${rotX.toFixed(2)}deg) rotateY(${rotY.toFixed(2)}deg) translateY(-4px)`;
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)';
      card.style.setProperty('--mouse-x', '-200px');
      card.style.setProperty('--mouse-y', '-200px');
    });
  });
}

/**
 * 5. Kinetic Scroll Reveals with Motion Engine
 */
function initKineticScrollReveals() {
  const revealTargets = document.querySelectorAll(
    '.section-header, .hero-content, .hero-meta-proof, .service-card, .feature-card, .step-card, .case-card, .matrix-card, .about-highlight-box, .stat-card'
  );

  revealTargets.forEach(el => {
    el.classList.add('kinetic-reveal');

    inView(el, () => {
      el.classList.add('revealed');
    }, { amount: 0.15 });
  });
}

/**
 * 6. Statistical Counter Tickers
 */
function initStatCounterTickers() {
  const counterElements = document.querySelectorAll('.proof-number, .stat-val-number, .stat-value');

  counterElements.forEach(el => {
    const rawText = el.textContent.trim();
    const match = rawText.match(/^([^\d]*)(\d+[\d,.]*)(.*)$/);
    if (!match) return;

    const prefix = match[1] || '';
    const numericStr = match[2].replace(/,/g, '');
    const suffix = match[3] || '';
    const targetVal = parseFloat(numericStr);
    if (isNaN(targetVal) || targetVal <= 0) return;

    const isInteger = !numericStr.includes('.');

    inView(el, () => {
      animate(0, targetVal, {
        duration: 1.6,
        ease: [0.16, 1, 0.3, 1], // expo out
        onUpdate: (latest) => {
          const displayVal = isInteger
            ? Math.round(latest).toLocaleString()
            : latest.toFixed(1);
          el.textContent = `${prefix}${displayVal}${suffix}`;
        }
      });
    }, { amount: 0.25 });
  });
}
